import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Mail, Image as ImageIcon, CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SRC = 'https://accounts.google.com/gsi/client';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Lazy-load the Google Identity Services script ONCE, on demand (never on
// initial public page load). Returns a promise that resolves when window.google
// is ready. The script + its CSS are injected only when the login UI is opened.
let gsiPromise = null;
const loadGsiScript = () => {
  if (typeof window !== 'undefined' && window.google?.accounts?.id) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => { gsiPromise = null; reject(new Error('gsi load failed')); });
      return;
    }
    const s = document.createElement('script');
    s.id = 'google-gsi-script';
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { gsiPromise = null; reject(new Error('gsi load failed')); };
    document.head.appendChild(s);
  });
  return gsiPromise;
};

// Only allow internal redirects (must start with "/", reject protocol-relative "//").
const sanitizeNext = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  return raw.startsWith('/') && !raw.startsWith('//') ? raw : '';
};

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const nextPath = sanitizeNext(searchParams.get('next'));

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const googleBtnRef = useRef(null);

  const handlePendingLike = async () => {
    const pendingImageId = sessionStorage.getItem('pendingLikeImageId');
    if (pendingImageId) {
      try {
        await axios.post(`${API_BASE}/images/${pendingImageId}/like`, {}, { withCredentials: true });
      } catch (_) { /* ignore */ } finally {
        sessionStorage.removeItem('pendingLikeImageId');
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    try {
      const res = await axios.post(
        `${API_BASE}/auth/google`,
        { credential: credentialResponse.credential },
        { withCredentials: true }
      );
      setUser(res.data.user);
      await new Promise((r) => setTimeout(r, 100));
      await checkAuth();
      await handlePendingLike();

      const profileComplete = res.data.user?.profile_complete;
      if (!profileComplete) {
        navigate(nextPath ? `/complete-profile?next=${encodeURIComponent(nextPath)}` : '/complete-profile', { replace: true });
      } else {
        navigate(nextPath || '/account', { replace: true });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Google sign-in failed. Please try again.');
    }
  };

  // Lazy-load Google Identity Services and render the official button only while
  // the login form is visible. One Tap is intentionally disabled (we never call
  // google.accounts.id.prompt()). Uses the ID-token (credential) flow — no
  // redirect URI involved.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  useEffect(() => {
    if (sent) return undefined;
    let cancelled = false;
    loadGsiScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleSuccess,
        });
        googleBtnRef.current.innerHTML = '';
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          width: 360,
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load Google sign-in. Please try the email option.');
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sent]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_RE.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = { email: trimmed };
      if (nextPath) payload.next = nextPath;
      await axios.post(`${API_BASE}/auth/magic-link/request`, payload);
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
            <ImageIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Explora</h1>
          <p className="text-gray-600">Sign in to upload and share your photos</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="magic-link-sent">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-50">
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Check your email</h2>
              <p className="text-gray-600">
                We sent a sign-in link to <strong>{email.trim().toLowerCase()}</strong>. It expires in 15 minutes.
              </p>
              <button
                type="button"
                data-testid="magic-link-try-different-email"
                onClick={() => { setSent(false); setEmail(''); }}
                className="text-sm text-blue-600 hover:underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Email magic-link form */}
              <form onSubmit={handleEmailLogin} className="space-y-3" data-testid="magic-link-form">
                <label htmlFor="login-email" className="sr-only">Email address</label>
                <Input
                  id="login-email"
                  data-testid="magic-link-email-input"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-base"
                />
                {error ? (
                  <p className="text-sm text-red-600" data-testid="magic-link-error-message">{error}</p>
                ) : null}
                <Button
                  type="submit"
                  data-testid="continue-with-email-button"
                  disabled={submitting}
                  className="w-full h-12 text-base font-medium bg-gray-900 text-white hover:bg-gray-800 flex items-center justify-center space-x-2"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Sending link…</span>
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5" />
                      <span>Continue with Email</span>
                    </>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Google Sign In — official button, rendered via lazy-loaded Google Identity Services */}
              <div className="flex justify-center" data-testid="continue-with-google-button">
                <div ref={googleBtnRef} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          By signing in, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
